import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Icon } from 'ui';
import { types } from 'utils/mappings';
import * as styles from './create-order.css';

const FailIcon = styled(ExclamationCircleFilled)`
  color: ${p => p.theme.colors?.alerts[1]} !important;
`;

const WarnIcon = styled(Icon)`
  color: ${p => p.theme.colors?.alerts[2]} !important;
`;

const FailDiv = styled.div`
  color: ${p => p.theme.colors?.alerts[1]} !important;
  font-size: 16px;
`;

const WarnDiv = styled.div`
  color: ${p => p.theme.colors?.alerts[2]} !important;
  font-size: 16px;
  ${p => p.ellipsis && `
    max-width: 300px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;`
  }
`;

const Date = styled.span`
  margin-left: 5px;
`;

const Label = styled.div`
  font-size: 10px;
`;

const Text = styled.div`
  font-size: 16px;
`;

const blockCheckTypes = {
  credit: 'credit',
  ecs: 'ecs',
  downPayment: 'downPayment',
};

const getWarningText = (type, isOk, intl) => {
  if (type === blockCheckTypes?.credit) {
    return intl.formatMessage({
      id: `orderWizard.CREDIT_CHECK${isOk ? '' : '_NOK'}`,
    });
  }
  if (type === blockCheckTypes?.ecs) {
    return intl.formatMessage({
      id: `orderWizard.ECS_CHECK${isOk ? '' : '_NOK'}`,
    });
  }
  if (type === blockCheckTypes?.downPayment) {
    return intl.formatMessage({
      id: 'orderWizard.DOWNPAYMENT_CHECK',
    });
  }
  return null;
};

const getBlockCheck = (blockCheckValue, intl, type) => {
  if (blockCheckValue === undefined || blockCheckValue === null) {
    return null;
  }
  if (blockCheckValue) {
    if (type === blockCheckTypes?.downPayment) {
      return (
        <WarnDiv ellipsis={true} className={styles.status}>
          <WarnIcon
            type={'exclamation-triangle'}
            className={styles.statusIcon}
          />
          {getWarningText(type, false, intl)}
        </WarnDiv>
      );
    }
    return (
      <div className={styles.status}>
        <CheckCircleFilled className={styles.statusIcon} />
        {getWarningText(type, true, intl)}
      </div>
    );
  }

  return (
    <FailDiv className={styles.status}>
      <FailIcon
        type={'exclamation-circle'}
        className={styles.statusIcon} theme='filled'
      />
      {getWarningText(type, false, intl)}
    </FailDiv>
  );
};

const getPoExpiry = (poExpiryDate, poExpiryCheck, poExpiryBlock, poExpiryWarning, intl) => {
  if (poExpiryCheck === undefined || poExpiryCheck === null) {
    return null;
  }
  if (poExpiryCheck) {
    return (
      <div className={styles.status}>
        <CheckCircleFilled className={styles.statusIcon} />
        {intl.formatMessage({ id: 'orderWizard.PO_NOT_EXPIRED' })}
        {poExpiryDate ?
          <Date>{intl.formatDate(poExpiryDate)}</Date> :
          null
        }
      </div>
    );
  } else if (poExpiryBlock) {
    return (
      <FailDiv className={styles.status}>
        <FailIcon className={styles.statusIcon} />
        {intl.formatMessage({ id: 'orderWizard.PO_EXPIRED' })}
        {poExpiryDate ?
          <Date>{intl.formatDate(poExpiryDate)}</Date> :
          null
        }
      </FailDiv>
    );
  } else if (poExpiryWarning) {
    return (
      <WarnDiv className={styles.status}>
        <WarnIcon
          type={'exclamation-triangle'}
          className={styles.statusIcon}
        />
        {intl.formatMessage({ id: 'orderWizard.PO_EXPIRED' })}
        {poExpiryDate ?
          <Date>{intl.formatDate(poExpiryDate)}</Date> :
          null
        }
      </WarnDiv>
    );
  }
  return null;
};

const NavContextItem = ({ item, intl }) =>
  <div className={styles.navContextItem}>
    <Label className={styles.type}>
      {intl.formatMessage({ id: types[item?.type?.toLowerCase()]?.displayTextTranslationKey })}
    </Label>
    <Text className={styles.text}>{item.line1}</Text>
    <Text>{item.line2}</Text>
    <Text>{item.line3}</Text>
    <Text>{item.line4}</Text>
    {getBlockCheck(item.creditCheck, intl, blockCheckTypes.credit)}
    {getBlockCheck(item.ecsBlock === null ? null : !item.ecsBlock, intl, blockCheckTypes.ecs)}
    {item?.downPayment && getBlockCheck(item.downPayment, intl, blockCheckTypes.downPayment)}
    {getPoExpiry(
      item.poExpiryDate,
      item.poExpiryCheck,
      item.poExpiryBlock,
      item.poExpiryWarning,
      intl,
    )}
  </div>;

NavContextItem.propTypes = {
  item: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
};

/* eslint-enable no-undefined */
export default injectIntl(NavContextItem);
